/**
 * The session token, in memory (KPR-1669; docs/plans/ios-app.md, Decision 2).
 *
 * Imports nothing but the platform (itself a leaf), so `utils/index.js`
 * (which decodes the token) and `utils/session.js` (which owns it) can both
 * read it without importing each other. Only `utils/session.js` writes here:
 * it hydrates the value from the platform's secure store once at boot, and
 * `setSessionToken` / `clearSessionToken` change it. Everything else reads
 * the session through `utils/session.js` (`getSessionToken`,
 * `getSessionUserId`) or the decoded helpers in `utils/index.js`, never from
 * storage: the Keychain the phone app keeps it in can only be read
 * asynchronously, and a render cannot wait.
 *
 * The one change made here and not by `utils/session.js` is another tab's:
 * the store reports it (the web's `storage` event; the app has no other tab),
 * the held token follows at once, and `utils/session.js` is told so it can
 * announce the change. It is followed here, where the token is held, so the
 * token is right for whoever reads it whether or not the session module has
 * loaded.
 */
import { secureStore } from '../platform';

/** The key the token is kept under in the platform's secure store. */
export const TOKEN_KEY = 'token';

/**
 * The key a device session's refresh credential (`kpd_…`, KPR-1674) is kept
 * under, beside the token. Only the phone app holds one: a browser's sign-in
 * starts no device session.
 */
export const REFRESH_KEY = 'deviceRefresh';

let token = null;
let refresh = null;
// Bumped by every change after load: hydration takes the stored value only
// when nothing changed while it was reading (utils/session.js).
let changes = 0;
const elsewhere = new Set();

const normalize = next => (typeof next === 'string' && next ? next : null);

/** The token this tab holds, or null. */
export function currentToken() {
	return token;
}

/** The device session's refresh credential this tab holds, or null (always null on the web). */
export function currentRefresh() {
	return refresh;
}

/**
 * utils/session.js only: a change made on this tab (a sign-in, a refresh, a
 * sign-out). The token and its refresh credential change together: a token
 * set without one holds none, so a credential never outlives its session.
 */
export function holdToken(next, nextRefresh = null) {
	changes += 1;
	token = normalize(next);
	refresh = token ? normalize(nextRefresh) : null;
}

/** utils/session.js only: what the boot read from the store, which is no change. */
export function restoreToken(next, nextRefresh = null) {
	token = normalize(next);
	refresh = token ? normalize(nextRefresh) : null;
}

/** How many changes the held token has had since load. */
export function tokenChanges() {
	return changes;
}

/** utils/session.js only: told after another tab changed the token. */
export function onTokenChangedElsewhere(listener) {
	elsewhere.add(listener);
	return () => elsewhere.delete(listener);
}

try {
	if (typeof secureStore?.subscribe === 'function') {
		secureStore.subscribe(TOKEN_KEY, value => {
			// The same token is no change: it keeps the refresh credential
			// held with it (only the app holds one, and it has no other tab).
			if (normalize(value) === token) changes += 1;
			else holdToken(value);
			[...elsewhere].forEach(listener => {
				try { listener(); } catch { /* one listener must not stop the rest */ }
			});
		});
	}
} catch { /* a store that cannot report: this tab follows only itself */ }
